import * as React from "react";
import { Grid, Menu, Tab, TabProps } from "semantic-ui-react";
import { useSelector } from "@teamprevent/admin-redux";

import { ApplicationState } from "../../../applicationState";
import { ADMIN_ROLES, Authorize } from "../../../auth";
import { AppState, PartialCustomerDetailModel } from "../model";
import { basePathSelector, customerSelector, routeIdSelector } from "../selectors";

import * as details from "./details";
import ContextItems from "./details/ContextItems";

import { Link, push } from "redux-little-router";

export interface CoreDetailProps {
    customer: PartialCustomerDetailModel;
}

export interface DetailProps extends CoreDetailProps {
    path: string;
    basePath: string;
    tab?: string;
}

export const Detail = () => {
    const currentTab = (state: AppState) => state.router.pathname!.substring(
        basePathSelector(state).length + 1 + routeIdSelector(state).toString().length);

    const customer = useSelector(customerSelector);
    const path = useSelector((state: ApplicationState) => state.router.pathname);
    const basePath = useSelector(basePathSelector);
    const tab = useSelector(currentTab);
    const tabProps = React.useMemo(() => ({ customer, path, basePath, tab }), [basePath, customer, path, tab]);

    const tabs = (Object.keys(details) as (keyof typeof details)[]).map(k => details[k]);
    const panes = tabs.map(t => t(tabProps));
    const activeIndex = panes.slice(1).findIndex(p => tab && tab.startsWith(p.path)) + 1;

    const content = () => <Tab panes={panes} onTabChange={handleTabChange} activeIndex={activeIndex} />;

    const handleTabChange = React.useCallback((event: any, { activeIndex: activeIndexFromTabProps }: TabProps) => {
        push(`${basePath}/${customer.id}${tabs[activeIndexFromTabProps as unknown as number](tabProps).path}`);
    }, [basePath, customer.id, tabProps, tabs]);

    return (
        <Grid container className="nomargintop">
            <Grid.Row>
                <Grid.Column>
                    <h1>{customer.name}</h1>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={3} as={Grid} padded>
                    <Grid.Column vertical as={Menu}>
                        <Menu.Item name="Customers" as={Link} href={basePath}>
                            All Customers
                        </Menu.Item>
                        <Menu.Item name="EmployeeRecords" as={Link} href={`${basePath}/${customer.id}/employeeRecords`}>
                            Employee Records
                        </Menu.Item>
                        <Menu.Item name="CustomerUsers" as={Link} href={`${basePath}/${customer.id}/customerUsers`}>
                            Customer Users
                        </Menu.Item>
                        <Menu.Item name="OrgStructures" as={Link} href={`${basePath}/${customer.id}/orgStructures`}>
                            Organisation Structure
                        </Menu.Item>
                        <Menu.Item name="Documents" as={Link} href={`${basePath}/${customer.id}/customerDocuments`}>
                            Documents
                        </Menu.Item>
                        <Authorize roles={[ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.SYSTEM_ADMIN]}>
                            <Menu.Item name="QuestionnaireConfig" as={Link} href={`${basePath}/${customer.id}/questionnaireConfig`}>
                                Form Customisation
                            </Menu.Item>
                        </Authorize>
                        <Menu.Item name="WorkGroups" as={Link} href={`${basePath}/${customer.id}/workGroups`}>
                            Work Groups
                        </Menu.Item>
                        {(customer?.assessmentAndWellBeingFeatures?.indexOf("Stress Risk Assessment") ?? -1) >= 0 &&
                            <Menu.Item
                                name="CustomerSraDepartments"
                                as={Link}
                                href={`${basePath}/${customer.id}/customerSraDepartments`}
                            >
                                SRA Departments
                            </Menu.Item>
                        }
                        <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN]}>
                            <Menu.Item name="CustomerTeams" as={Link} href={`${basePath}/${customer.id}/teams`}>
                                Customer Teams
                            </Menu.Item>
                        </Authorize>
                        <ContextItems customer={customer} />
                    </Grid.Column>
                </Grid.Column>
                <Grid.Column width={13}>
                    {content()}
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
